import { Logger, LoggerService } from '@Shared/logger';
import { LocalStorage, CookiesStorageClass } from '@Shared/storage';
import { LocalCheckResult, ServerSession } from '@Modules/index';
import { DomainUtil } from '@Shared/utils';
import { HttpService } from '@Shared/http';
import { STORAGE } from '@Core';

export abstract class SessionServerRepository {
  protected Logger: Logger = LoggerService.createLogger(
    'SessionServerRepository',
  );

  private _localStorageManger: LocalStorage;
  private _cookieManager: CookiesStorageClass;

  private serverSession: ServerSession;

  private SESSION_PATH = '/api/session';
  private readonly SESSION_REQUEST_INIT = { credentials: 'include' } as const;
  // Logout fires this DELETE and then immediately navigates to Auth0's /v2/logout.
  // A normal fetch is aborted when the document is torn down — and since DELETE
  // needs a CORS preflight, it's two round trips that rarely finish in time. When it
  // loses that race the server session survives logout, and the next page load
  // restores the user from it via getAuth0Session(). keepalive lets the request
  // outlive the document so no caller has to await it before redirecting.
  private readonly SESSION_DELETE_REQUEST_INIT = {
    credentials: 'include',
    keepalive: true,
  } as const;
  private readonly SESSION_UPDATE_REQUEST_INIT = {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  } as const;
  get ServerSession(): ServerSession {
    return this.serverSession;
  }

  private localCheckResult: LocalCheckResult;
  get LocalCheckResult(): LocalCheckResult {
    return this.localCheckResult;
  }

  get idToken(): string {
    return this.serverSession.idToken;
  }

  constructor() {
    this.Logger.trace('****  CTOR ****');
    this._localStorageManger = new LocalStorage();
  }

  protected getLocalStorageSession(): ServerSession {
    try {
      return this._localStorageManger.getParsed<ServerSession>(
        STORAGE.sessionLocalStorageKey,
      );
    } catch (error) {
      this.Logger.error(
        'Error getting local session:',
        error instanceof Error ? error.message : error,
      );
    }
    return {} as ServerSession;
  }

  protected saveLocalStorageSession(sessionLocalStorage: string) {
    try {
      this._localStorageManger.set(
        STORAGE.sessionLocalStorageKey,
        sessionLocalStorage,
      );
      this.Logger.info('Local session was set');
    } catch (e) {
      this.Logger.error('Error during saveLocalStorageSession process... ', e);
    }
  }

  protected saveCookieSession = (entitlementToken: string): void => {
    try {
      if (!entitlementToken) {
        throw new Error('Entitlement token is empty or undefined');
      }
      this._cookieManager.set(STORAGE.entitlementCookieKey, entitlementToken, {
        daysOfLife: 365,
        useRootDomain: true,
        path: '/',
        secure: true,
      });
      this.Logger.info('Cookie session saved successfully');
    } catch (error) {
      this.Logger.error(
        'Unable to save session to cookie storage:',
        error instanceof Error ? error.message : error,
      );
    }
  };

  protected async getServerSession(): Promise<ServerSession> {
    this.Logger.info('Getting server session...');
    try {
      const baseUrl = `https://session.${DomainUtil.getRootDomain()}`;
      const httpService = new HttpService(baseUrl, 0);
      const session = await httpService.get<ServerSession>(
        this.SESSION_PATH,
        undefined,
        undefined,
        this.SESSION_REQUEST_INIT,
      );
      this.serverSession = session;
      return session;
    } catch (error) {
      this.Logger.error('Error fetching server session: ', error);
      throw error;
    }
  }

  protected async updateServerSession(newServerSession: ServerSession) {
    try {
      const baseUrl = `https://session.${DomainUtil.getRootDomain()}`;
      const httpService = new HttpService(baseUrl, 0);
      await httpService.post<ServerSession>(
        this.SESSION_PATH,
        newServerSession,
        undefined,
        this.SESSION_UPDATE_REQUEST_INIT,
      );
    } catch (e) {
      this.Logger.error('Failed to update session server: ', e);
      const message = e instanceof Error ? e.message : String(e);
      throw new Error(`Server session error during update: ${message}`);
    }
  }

  protected async deleteServerSession() {
    try {
      const baseUrl = `https://session.${DomainUtil.getRootDomain()}`;
      const httpService = new HttpService(baseUrl, 0);
      await httpService.delete(
        this.SESSION_PATH,
        undefined,
        this.SESSION_DELETE_REQUEST_INIT,
      );
      return true;
    } catch (e) {
      this.Logger.error('Failed to delete server session: ', e);
      const message = e instanceof Error ? e.message : String(e);
      throw new Error(`Server session error during deletion: ${message}`);
    }
  }
}
